/**
 * Die Führungsanzeige: oben das nächste Manöver, unten Tempo, Tempolimit und
 * was noch bleibt. Sie legt sich über die Karte und schaltet die Seitenleiste
 * beiseite - beim Fahren zählt ein Blick, nicht eine Liste.
 *
 * Mit dem Velo zählt anderes als im Auto. Statt des Tempolimits steht die
 * Steigung da, und statt der Tankstellen stehen Brunnen und Pumpen im Kasten:
 * ein Velo kennt kein Limit, aber sehr wohl eine Rampe, und wer durstig ist,
 * sucht keinen Diesel.
 */
import { createNavigation, strecke, dauer, distanz } from '../data/navigation.js';
import {
  routeVehicle, fetchSpeedLimits, fetchHoehenprofil,
  anstiege, steigungBei, restAufstieg
} from '../data/routing.js';
import {
  tankstellenEntlang, parkenBei, veloRastEntlang, veloParkenBei
} from '../data/unterwegs.js';
import { toast } from './toast.js';

/**
 * Sprachausgabe gibt es nicht überall: in der Android-WebView fehlt
 * `speechSynthesis` ganz. Ein nackter Aufruf wirft dort, und zwar auch beim
 * Beenden - die Anzeige liess sich dann nicht mehr schliessen.
 */
const stimme = () => (typeof globalThis.speechSynthesis === 'object' ? globalThis.speechSynthesis : null);

/** Valhalla-Manövertypen -> Pfeil. Nur die Richtung, nicht jede Feinheit. */
const PFEILE = {
  1: '↑', 2: '↑', 3: '↑', 4: '◆', 5: '◆', 6: '◆',
  9: '↗', 10: '→', 11: '↘', 12: '↱', 13: '↰',
  14: '↙', 15: '←', 16: '↖', 17: '↑', 18: '↑',
  19: '↗', 20: '↖', 21: '↗', 22: '↑', 23: '↗', 24: '↖',
  25: '↱', 26: '⟳', 27: '⤷', 37: '↑'
};

const SPRACHE = 'de-CH';

export function createNavOverlay({ map, onStart, onEnd }) {
  let nav = null;
  let root = null;
  let ziel = null;
  let modus = 'auto';
  let stumm = false;
  let letzteAnsage = '';
  let tankstellen = [];       // mit `entlang` versehen, nach Strecke sortiert
  let parkplaetze = null;     // null = noch nicht geholt
  let parkenGezeigt = false;
  let velo = null;            // Velotyp und Regler, damit sie eine Neuberechnung überleben
  let profil = null;          // Höhenprofil der laufenden Route
  let rampen = [];            // Anstiege daraus
  let angesagt = new Set();   // welche Rampen schon angekündigt sind
  let rast = [];              // Brunnen, Pumpen, Läden - mit `entlang`
  let veloplaetze = null;
  const el = {};

  function sprich(text) {
    if (stumm || !text || text === letzteAnsage) return;
    letzteAnsage = text;
    const s = stimme();
    if (!s) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = SPRACHE;
      u.rate = 1.05;
      s.cancel();
      s.speak(u);
    } catch { /* ohne Stimme bleibt die Anzeige */ }
  }

  function bauen() {
    root = document.createElement('div');
    root.className = 'nav';

    const oben = document.createElement('div');
    oben.className = 'nav__banner';
    el.pfeil = document.createElement('div');
    el.pfeil.className = 'nav__arrow';
    const textbox = document.createElement('div');
    textbox.className = 'nav__texts';
    el.distanz = document.createElement('div');
    el.distanz.className = 'nav__dist';
    el.anweisung = document.createElement('div');
    el.anweisung.className = 'nav__instr';
    textbox.append(el.distanz, el.anweisung);
    oben.append(el.pfeil, textbox);

    const unten = document.createElement('div');
    unten.className = 'nav__bar';

    el.tempo = document.createElement('div');
    el.tempo.className = 'nav__speed';
    el.tempo.innerHTML = '<strong>–</strong><span>km/h</span>';

    el.limit = document.createElement('div');
    el.limit.className = 'nav__limit';
    el.limit.hidden = true;

    // Die Steigung steht an derselben Stelle wie das Tempolimit, sieht aber
    // bewusst anders aus: kein rundes Schild, sondern eine Zahl mit Pfeil.
    el.steigung = document.createElement('div');
    el.steigung.className = 'nav__grade';
    el.steigung.hidden = true;

    el.rest = document.createElement('div');
    el.rest.className = 'nav__rest';

    const knoepfe = document.createElement('div');
    knoepfe.className = 'nav__btns';
    el.ton = document.createElement('button');
    el.ton.type = 'button';
    el.ton.className = 'nav__btn';
    el.ton.textContent = 'Ton aus';
    el.ton.addEventListener('click', () => {
      stumm = !stumm;
      el.ton.textContent = stumm ? 'Ton an' : 'Ton aus';
      if (stumm) stimme()?.cancel();
    });
    // Ein Knopf, der nichts bewirkt, ist schlimmer als keiner.
    el.ton.hidden = !stimme();
    const schluss = document.createElement('button');
    schluss.type = 'button';
    schluss.className = 'nav__btn nav__btn--end';
    schluss.textContent = 'Beenden';
    schluss.addEventListener('click', () => beenden());
    knoepfe.append(el.ton, schluss);

    unten.append(el.tempo, el.limit, el.steigung, el.rest, knoepfe);

    // Unterwegs-Kasten: klein und zu, bis man ihn braucht. Zu Fuss bleibt er
    // ganz weg - auf einem Spaziergang sucht niemand eine Zapfsäule.
    el.box = document.createElement('div');
    el.box.className = 'nav__box';
    el.box.hidden = true;
    el.boxListe = document.createElement('div');
    el.boxListe.className = 'nav__box-list';
    el.boxListe.hidden = true;

    el.reiter = document.createElement('div');
    el.reiter.className = 'nav__box-tabs';
    el.box.append(el.reiter, el.boxListe);

    root.append(oben, el.box, unten);
    // In die Kartenfläche, nicht ans Fenster: so liegt die Führung genau über
    // der Karte und nicht über den Spalten daneben. Wird rechts die Route
    // eingeblendet oder links die Darstellung, rückt die Anzeige mit.
    map.getContainer().append(root);
  }

  let letztePosition = null;
  let offen = null;          // 'tanken' | 'parken' | null

  /**
   * Welche Reiter der Kasten zeigt. Die Reihenfolge ist die der Dringlichkeit
   * unterwegs: erst was man im Fahren braucht, dann was am Ziel wartet.
   */
  const REITER = {
    auto: [['tanken', 'Tanken'], ['parken', 'Parken']],
    bicycle: [['wasser', 'Wasser'], ['service', 'Pumpe'], ['veloparken', 'Abstellen']]
  };

  function reiterSetzen() {
    el.reiter.textContent = '';
    for (const [schluessel, text] of REITER[modus] || []) {
      el.reiter.append(kastenKnopf(text, schluessel));
    }
  }

  function kastenKnopf(text, welches) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'nav__box-tab';
    b.dataset.welches = welches;
    b.textContent = text;
    b.addEventListener('click', () => {
      offen = offen === welches ? null : welches;
      if (offen === 'parken' && parkplaetze === null) parkenHolen();
      if (offen === 'veloparken' && veloplaetze === null) veloParkenHolen();
      zeichneKasten();
    });
    return b;
  }

  /** Was hinter einem Reiter steckt: die Liste und der Text darunter. */
  function eintraegeFuer(welches) {
    const voraus = (liste) => liste.filter((t) => t.entlang > letzterFortschritt + 150).slice(0, 6);
    if (welches === 'tanken') return voraus(tankstellen);
    if (welches === 'wasser') return voraus(rast.filter((r) => r.art === 'wasser'));
    if (welches === 'service') return voraus(rast.filter((r) => r.art === 'service'));
    if (welches === 'parken') return (parkplaetze || []).slice(0, 6);
    if (welches === 'veloparken') return (veloplaetze || []).slice(0, 6);
    return [];
  }

  /** Zweite Zeile eines Eintrags - je Art etwas anderes. */
  function untertitel(e, welches) {
    if (welches === 'tanken') {
      return `${strecke(e.entlang - letzterFortschritt)} voraus${e.zeiten === '24/7' ? ' · 24 h' : ''}`;
    }
    if (welches === 'wasser') {
      return `${strecke(e.entlang - letzterFortschritt)} voraus`;
    }
    if (welches === 'service') {
      return [`${strecke(e.entlang - letzterFortschritt)} voraus`,
              e.pumpe ? 'Pumpe' : null,
              e.werkzeug ? 'Werkzeug' : null,
              e.laden ? 'Laden' : null]
        .filter(Boolean).join(' · ');
    }
    if (welches === 'veloparken') {
      return [e.ueberdacht ? 'überdacht' : null,
              e.abschliessbar ? 'abschliessbar' : null,
              e.plaetze ? `${e.plaetze} Pl.` : null,
              e.gebuehr || null]
        .filter(Boolean).join(' · ') || 'ohne Angaben';
    }
    const art = e.ueberdacht ? 'Parkhaus' : 'Parkplatz';
    // Trägt der Eintrag keinen Namen, steht die Art schon in der Überschrift -
    // dann nicht zweimal dasselbe.
    return [e.name === art ? null : art,
            e.plaetze ? `${e.plaetze} Pl.` : null,
            e.gebuehr === 'gratis' ? 'gratis' : null]
      .filter(Boolean).join(' · ') || 'ohne Angaben';
  }

  /** Text, wenn nichts da ist - der Grund ist je Art ein anderer. */
  function leerText(welches) {
    if (welches === 'tanken') return 'Keine Tankstelle mehr voraus.';
    if (welches === 'wasser') return 'Kein Brunnen mehr voraus.';
    if (welches === 'service') return 'Keine Pumpe mehr voraus.';
    const noch = welches === 'parken' ? parkplaetze === null : veloplaetze === null;
    return noch ? 'Suche …' : 'Nichts gefunden.';
  }

  /** Inhalt des Kastens - je nachdem, was offen ist. */
  function zeichneKasten() {
    if (!el.box) return;
    el.box.hidden = !REITER[modus];
    for (const b of el.reiter.children) {
      b.classList.toggle('is-open', offen === b.dataset.welches);
    }
    el.boxListe.hidden = !offen;
    if (!offen) return;

    el.boxListe.textContent = '';
    const eintraege = eintraegeFuer(offen);

    if (!eintraege.length) {
      const leer = document.createElement('div');
      leer.className = 'nav__box-empty';
      leer.textContent = leerText(offen);
      el.boxListe.append(leer);
      return;
    }

    for (const e of eintraege) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'nav__box-item';
      const name = document.createElement('strong');
      name.textContent = e.name;
      const sub = document.createElement('small');
      sub.textContent = untertitel(e, offen);
      b.append(name, sub);
      b.addEventListener('click', () => waehlen(e));
      el.boxListe.append(b);
    }
  }

  /**
   * Tankstelle oder Brunnen = Zwischenhalt, die Fahrt geht danach weiter zum
   * Ziel. Parkhaus oder Veloparkplatz = neues Ziel, dort steigt man ab. Zwei
   * verschiedene Absichten, deshalb zwei verschiedene Wirkungen - woran man
   * sie unterscheidet, steht im Eintrag: nur was unterwegs liegt, trägt ein
   * `entlang`.
   */
  function waehlen(e) {
    offen = null;
    zeichneKasten();
    const punkt = { lat: e.lat, lng: e.lng };
    if (e.entlang !== undefined) {
      zwischenhalt = punkt;
      sprich(`Zwischenhalt ${e.name} eingefügt.`);
    } else {
      ziel = punkt;
      zwischenhalt = null;
      sprich(`Ziel geändert auf ${e.name}.`);
    }
    neuBerechnen(letztePosition || punkt);
  }

  let zwischenhalt = null;
  let letzterFortschritt = 0;
  let schlussTimer = null;
  let rechnetNeu = false;
  /** Zählt die Routen einer Führung: eine späte Antwort gilt nur für ihre eigene. */
  let routenStand = 0;

  function zeigen(d) {
    letztePosition = d.position;
    // Am Zwischenhalt vorbei: eine spätere Neuberechnung soll nicht dorthin zurück.
    if (zwischenhalt && distanz(d.position, zwischenhalt) < 40) zwischenhalt = null;
    el.pfeil.textContent = PFEILE[d.manoever.typ] || '↑';
    el.distanz.textContent = strecke(d.bisManoever);
    el.anweisung.textContent = d.manoever.text;

    const kmh = d.tempoKmh;
    el.tempo.querySelector('strong').textContent =
      kmh === null ? '–' : String(Math.round(kmh));

    if (modus === 'bicycle') steigungZeigen(d);

    if (d.limit && modus !== 'bicycle') {
      el.limit.hidden = false;
      el.limit.textContent = String(d.limit);
      // Nur einfärben, wenn die Messung auch taugt - unter 3 km/h ist sie Rauschen.
      const zuSchnell = kmh !== null && kmh > 3 && kmh > d.limit + 5;
      el.limit.classList.toggle('is-over', zuSchnell);
    } else {
      el.limit.hidden = true;
    }

    letzterFortschritt = d.entlang;
    if (offen === 'tanken' || offen === 'wasser' || offen === 'service') zeichneKasten();
    // Abstellmöglichkeiten im Voraus holen, damit sie beim Antippen schon da
    // sind. Mit dem Velo erst später: 500 Meter vor dem Ziel, weil ein
    // Veloständer näher am Eingang steht als ein Parkhaus.
    if (!parkenGezeigt && modus === 'auto' && d.uebrig < 3000) {
      parkenGezeigt = true;
      parkenHolen();
    }
    if (!parkenGezeigt && modus === 'bicycle' && d.uebrig < 800) {
      parkenGezeigt = true;
      veloParkenHolen();
    }

    const ankunft = new Date(Date.now() + d.restSekunden * 1000);
    const rest = [strecke(d.uebrig), dauer(d.restSekunden),
                  `an ${String(ankunft.getHours()).padStart(2, '0')}:${String(ankunft.getMinutes()).padStart(2, '0')}`];
    // Mit dem Velo gehören die Höhenmeter dazu, die noch kommen - sie sagen
    // über die verbleibende Mühe mehr aus als die Kilometer.
    if (modus === 'bicycle' && profil) {
      const rauf = restAufstieg(profil, d.entlang);
      if (rauf >= 10) rest.push(`▲ ${rauf} m`);
    }
    el.rest.textContent = rest.join(' · ');

    // Karte mitführen: Blick in Fahrtrichtung, geneigt.
    map.easeTo({
      center: [d.position.lng, d.position.lat],
      bearing: d.kurs ?? map.getBearing(),
      pitch: 55,
      zoom: Math.max(16, map.getZoom()),
      duration: 900,
      essential: true
    });

    if (d.amZiel && !schlussTimer) {
      sprich('Sie haben Ihr Ziel erreicht.');
      schlussTimer = setTimeout(beenden, 2500);
    }
  }

  /** Parkmöglichkeiten einmalig holen; der Kasten zeigt sie beim Öffnen. */
  async function parkenHolen() {
    if (parkplaetze !== null) return;
    parkplaetze = [];
    try {
      parkplaetze = await parkenBei(ziel);
    } catch { /* ohne Vorschläge bleibt die Führung */ }
    if (offen === 'parken') zeichneKasten();
  }

  /**
   * Steigung anzeigen und den nächsten Anstieg ankündigen.
   *
   * Angesagt wird 400 Meter vorher - weit genug, um noch zu schalten, und nah
   * genug, dass man die Rampe dann auch sieht. Eine Ansage pro Anstieg: eine
   * Passstrasse hat zwanzig Kehren, aber nur einen Anstieg.
   */
  function steigungZeigen(d) {
    const st = profil ? steigungBei(profil, d.entlang) : null;
    if (st === null) {
      el.steigung.hidden = true;
    } else {
      el.steigung.hidden = false;
      const pfeil = st >= 1 ? '▲' : st <= -1 ? '▼' : '·';
      el.steigung.textContent = `${pfeil} ${Math.abs(Math.round(st))} %`;
      el.steigung.classList.toggle('is-steil', st >= 7);
    }

    for (const [i, r] of rampen.entries()) {
      if (angesagt.has(i)) continue;
      if (r.bis <= d.entlang) { angesagt.add(i); continue; }   // schon vorbei
      const bis = r.von - d.entlang;
      if (bis > 400) break;                                     // kommt noch
      // Eine Abbiegeansage ist wichtiger als eine Rampe. Steht die Abzweigung
      // unmittelbar bevor, wartet der Anstieg auf den nächsten Durchlauf.
      if (d.bisManoever < 150) break;
      angesagt.add(i);
      const prozent = Math.round(r.steigung);
      sprich(bis > 50
        ? `In ${strecke(bis)} beginnt ein Anstieg über ${strecke(r.laenge)} mit ${prozent} Prozent.`
        : `Anstieg über ${strecke(r.bis - d.entlang)} mit ${prozent} Prozent.`);
      break;
    }
  }

  /** Veloabstellplätze am Ziel - dieselbe Mechanik wie beim Parkhaus. */
  async function veloParkenHolen() {
    if (veloplaetze !== null) return;
    veloplaetze = [];
    try {
      veloplaetze = await veloParkenBei(ziel);
    } catch { /* ohne Vorschläge bleibt die Führung */ }
    if (offen === 'veloparken') zeichneKasten();
  }

  async function neuBerechnen(von) {
    if (!ziel || rechnetNeu) return;
    rechnetNeu = true;
    const dieseFuehrung = nav;
    sprich('Route wird neu berechnet.');
    try {
      // `velo` muss mit: sonst führt die neue Route nach Valhallas Vorgaben
      // statt nach dem eingestellten Velotyp - wer mit dem Rennvelo gestartet
      // ist, bekäme nach einem Verfahrer plötzlich Kieswege.
      const res = await routeVehicle({
        from: von, to: ziel, via: zwischenhalt, costing: modus,
        velo: modus === 'bicycle' ? velo : undefined
      });
      const limits = await fetchSpeedLimits({ shape: res.shape, costing: modus }).catch(() => []);
      // Inzwischen beendet: die Antwort gehört zu keiner Führung mehr.
      if (nav !== dieseFuehrung || !nav) return;
      routenStand += 1;
      zeichneRoute(res.coordinates);
      nav.ersetzen({ ...res, limits });
      if (modus === 'bicycle') profilHolen(res.shape);
    } catch {
      sprich('Neue Route nicht möglich.');
    } finally {
      rechnetNeu = false;
    }
  }

  /**
   * Höhenprofil der laufenden Route holen und die Anstiege daraus bestimmen.
   * Beides ist Beiwerk: die Führung läuft auch ohne, dann bleibt das
   * Steigungsfeld eben leer.
   */
  async function profilHolen(shape) {
    profil = null;
    rampen = [];
    angesagt = new Set();
    const stand = routenStand;
    try {
      const neu = await fetchHoehenprofil({ shape });
      if (stand !== routenStand) return;
      profil = neu;
      rampen = anstiege(profil);
    } catch { /* ohne Profil keine Steigung */ }
  }

  function zeichneRoute(coordinates) {
    const data = {
      type: 'FeatureCollection',
      features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates }, properties: {} }]
    };
    if (map.getSource('route')) map.getSource('route').setData(data);
  }

  async function starten({ route, to, costing, velo: veloEinstellung }) {
    ziel = to;
    modus = costing || 'auto';
    velo = veloEinstellung || null;
    if (!route?.maneuvers?.length) return;

    if (!root) bauen();
    root.hidden = false;
    // Die Anzeige überlebt die Fahrt: ohne Zurücksetzen stünde beim nächsten
    // Mal im Auto noch die Steigung der letzten Velofahrt da.
    el.steigung.hidden = true;
    el.limit.hidden = true;
    document.body.classList.add('is-navigating');
    onStart?.();

    profil = null;
    rampen = [];
    angesagt = new Set();
    clearTimeout(schlussTimer);
    schlussTimer = null;

    // Bis die erste Ortung kommt, steht schon das erste Manöver da - eine leere
    // schwarze Fläche sähe aus, als wäre die Führung gescheitert.
    const erstes = route.maneuvers[0];
    el.pfeil.textContent = PFEILE[erstes.typ] || '↑';
    el.distanz.textContent = 'Suche Standort …';
    el.anweisung.textContent = erstes.text;
    el.rest.textContent = '';
    el.tempo.querySelector('strong').textContent = '–';

    // Sofort führen. Die Führung lief bisher erst nach den Tempolimits an: wer in
    // diesem Moment „Beenden“ drückte, bekam sie danach trotzdem, ohne Anzeige.
    const dieseFuehrung = createNavigation({
      onUpdate: zeigen,
      onAnnounce: sprich,
      onOffRoute: neuBerechnen,
      onOrtungsfehler: (verweigert) => {
        if (verweigert) {
          toast('Ortung ist nicht erlaubt - ohne Standort keine Führung.', 5000);
          beenden();
        } else if (letztePosition === null) {
          el.distanz.textContent = 'Kein GPS-Empfang …';
        }
      }
    });
    nav = dieseFuehrung;
    letztePosition = null;
    nav.start({ ...route, limits: [] });
    sprich(erstes.ansageJetzt || 'Navigation gestartet.');

    // Mit dem Auto die Tempolimits, mit dem Velo das Höhenprofil - beides ist
    // Beiwerk und kommt nach, sobald es da ist.
    // Die Limits nennen Punktnummern dieser Route - nach einer Neuberechnung
    // gehörten sie an falsche Stellen.
    routenStand += 1;
    const stand = routenStand;
    if (modus === 'bicycle') profilHolen(route.shape);
    else fetchSpeedLimits({ shape: route.shape, costing: modus })
      .then((limits) => { if (nav === dieseFuehrung && stand === routenStand) nav.limitsSetzen(limits); })
      .catch(() => {});

    // Was unterwegs liegt, einmalig holen und auf die Strecke projizieren.
    // Luftlinie hilft nicht - entscheidend ist, wann man daran vorbeikommt.
    tankstellen = [];
    rast = [];
    parkplaetze = null;
    veloplaetze = null;
    parkenGezeigt = false;
    zwischenhalt = null;
    letzterFortschritt = 0;
    offen = null;
    reiterSetzen();
    zeichneKasten();
    try {
      if (modus === 'bicycle') {
        const liste = await veloRastEntlang(route.coordinates);
        if (nav !== dieseFuehrung) return;
        rast = entlangLegen(liste, 250);
      } else if (modus === 'auto') {
        const liste = await tankstellenEntlang(route.coordinates);
        if (nav !== dieseFuehrung) return;
        tankstellen = entlangLegen(liste, 400);
      }
      zeichneKasten();
    } catch { /* ohne diese Liste führt die Navigation trotzdem */ }
  }

  /**
   * Punkte in der Nähe der Route auf die Route legen: wie weit vom Start
   * entfernt kommt man daran vorbei, und wie weit liegt der Punkt abseits.
   * Was weiter abseits liegt als `abseitsMax`, fällt heraus - ein Brunnen
   * zweihundert Meter neben dem Weg ist kein Brunnen am Weg.
   */
  function entlangLegen(liste, abseitsMax) {
    return liste
      .map((t) => {
        const p = nav.entlangWert(t);
        return p ? { ...t, entlang: p.entlang, abseits: p.abstand } : null;
      })
      .filter((t) => t && t.abseits < abseitsMax)
      .sort((a, b) => a.entlang - b.entlang);
  }

  function beenden() {
    if (!nav) return;
    nav.stop();
    nav = null;
    clearTimeout(schlussTimer);
    schlussTimer = null;
    rechnetNeu = false;
    stimme()?.cancel();
    if (root) root.hidden = true;
    document.body.classList.remove('is-navigating');
    map.easeTo({ pitch: 0, bearing: 0, duration: 600 });
    onEnd?.();
  }

  return { starten, beenden, laeuft: () => Boolean(nav) };
}
